import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MediaLibraryService } from './media-library.service';

/**
 * Centralized metadata auto-save service
 * Handles debouncing and merging of metadata updates to prevent multiple API calls
 */
@Injectable({
  providedIn: 'root'
})
export class MetadataAutoSaveService {
  private mediaLibraryService = inject(MediaLibraryService);

  // Accumulator for pending metadata changes by mediaAssetUuid
  private pendingChanges = new Map<string, Record<string, any>>();

  // Save trigger subject
  private saveTrigger$ = new Subject<string>();

  constructor() {
    // Setup debounced save handler - waits 1.5 seconds after last change
    this.saveTrigger$.pipe(
      debounceTime(1500)
    ).subscribe(mediaAssetUuid => {
      this.performSave(mediaAssetUuid);
    });
  }

  /**
   * Queue a metadata update (debounced)
   * Merges with any pending changes for the same asset
   * @param mediaAssetUuid The media asset UUID
   * @param metadata Partial metadata to update
   */
  queueMetadataUpdate(mediaAssetUuid: string, metadata: Record<string, any>): void {
    if (!mediaAssetUuid) {
      console.warn('[MetadataAutoSave] No mediaAssetUuid provided, skipping save');
      return;
    }

    // Get existing pending changes or create new object
    const existingChanges = this.pendingChanges.get(mediaAssetUuid) || {};

    // Merge new metadata with existing (later changes override earlier ones)
    const mergedMetadata = {
      ...existingChanges,
      ...metadata
    };

    // Store merged metadata
    this.pendingChanges.set(mediaAssetUuid, mergedMetadata);

    // Trigger debounced save
    this.saveTrigger$.next(mediaAssetUuid);

    console.log('[MetadataAutoSave] Queued metadata update for', mediaAssetUuid, '- fields:', Object.keys(metadata));
  }

  /**
   * Perform the actual save operation (called after debounce)
   * Compiles all pending changes into a single API call
   */
  private performSave(mediaAssetUuid: string): void {
    const pendingMetadata = this.pendingChanges.get(mediaAssetUuid);

    if (!pendingMetadata || Object.keys(pendingMetadata).length === 0) {
      console.log('[MetadataAutoSave] No pending changes for', mediaAssetUuid);
      return;
    }

    console.log('[MetadataAutoSave] Saving compiled metadata for', mediaAssetUuid, '- fields:', Object.keys(pendingMetadata));

    this.mediaLibraryService.updateMediaAssetMetadata(mediaAssetUuid, pendingMetadata).subscribe({
      next: () => {
        console.log('[MetadataAutoSave] Successfully saved metadata for', mediaAssetUuid);
        // Clear pending changes after successful save
        this.pendingChanges.delete(mediaAssetUuid);
      },
      error: (error) => {
        console.error('[MetadataAutoSave] Error saving metadata for', mediaAssetUuid, ':', error);
        // Keep pending changes in case of error (they'll be retried on next update)
      }
    });
  }

  /**
   * Force immediate save of all pending changes (bypasses debounce)
   * Useful before navigation or critical operations
   */
  forceSaveAll(): void {
    console.log('[MetadataAutoSave] Force saving all pending changes');
    this.pendingChanges.forEach((metadata, mediaAssetUuid) => {
      this.performSave(mediaAssetUuid);
    });
  }

  /**
   * Force immediate save for a specific asset (bypasses debounce)
   */
  forceSave(mediaAssetUuid: string): void {
    console.log('[MetadataAutoSave] Force saving for', mediaAssetUuid);
    this.performSave(mediaAssetUuid);
  }

  /**
   * Clear pending changes for an asset (e.g., when switching videos)
   */
  clearPending(mediaAssetUuid: string): void {
    this.pendingChanges.delete(mediaAssetUuid);
    console.log('[MetadataAutoSave] Cleared pending changes for', mediaAssetUuid);
  }

  /**
   * Clear all pending changes
   */
  clearAllPending(): void {
    this.pendingChanges.clear();
    console.log('[MetadataAutoSave] Cleared all pending changes');
  }

  /**
   * Check if there are pending changes for an asset
   */
  hasPendingChanges(mediaAssetUuid: string): boolean {
    const pending = this.pendingChanges.get(mediaAssetUuid);
    return pending !== undefined && Object.keys(pending).length > 0;
  }
}
